import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FEATURE_NAMES, SYSTEM_FIELD_CONFIG } from "@/lib/plans/enforce";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Schema simplificado - FleetONE apenas com veículos
const generatePlanSchema = (systemCode: string) => {
  return z.object({
    name: z.string().min(1, "Nome é obrigatório"),
    description: z.string().optional(),
    price_monthly: z.number().min(0, "Preço mensal deve ser positivo"),
    price_yearly: z.number().min(0, "Preço anual deve ser positivo"),
    features: z.object({
      limits: z.object({
        fleet_vehicles_max: z.number().min(1, "Mínimo 1 veículo"),
      }),
      toggles: z.object({}).optional(),
    }),
  });
};

type CreatePlanData = z.infer<ReturnType<typeof generatePlanSchema>>;

interface CreatePlanModalProps {
  open: boolean;
  onClose: () => void;
  systemCode: string;
  systemName: string;
}

export function CreatePlanModal({ open, onClose, systemCode, systemName }: CreatePlanModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const createPlanMutation = useMutation({
    mutationFn: async (data: CreatePlanData) => {
      // First get system ID
      const { data: system, error: systemError } = await supabase
        .from('systems')
        .select('id')
        .eq('code', systemCode)
        .single();

      if (systemError || !system) throw new Error('Sistema não encontrado');

      const { error } = await supabase
        .from('plans')
        .insert({
          system_id: system.id,
          name: data.name,
          description: data.description,
          price_monthly: data.price_monthly,
          price_yearly: data.price_yearly,
          features: data.features,
          is_active: true,
          sort_order: 0
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-plans'] });
      toast.success('Plano criado com sucesso!');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erro ao criar plano');
    },
  });

  const form = useForm<CreatePlanData>({
    resolver: zodResolver(generatePlanSchema(systemCode)),
    defaultValues: {
      name: "",
      description: "",
      price_monthly: 0,
      price_yearly: 0,
      features: {
        limits: {
          fleet_vehicles_max: 10,
        },
        toggles: {},
      },
    },
  });

  const onSubmit = async (data: CreatePlanData) => {
    setIsSubmitting(true);
    try {
      await createPlanMutation.mutateAsync(data);
    } catch (error) {
      console.error("Error creating plan:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Criar Novo Plano - {systemName}</DialogTitle>
          <DialogDescription>
            Configure um novo plano para o sistema {systemName}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome do Plano</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Básico, Profissional, Enterprise" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Descrição do plano..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="price_monthly"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Mensal (R$)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0.00" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="price_yearly"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Preço Anual (R$)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          placeholder="0.00" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Limite de Veículos</h3>
              <FormField
                control={form.control}
                name="features.limits.fleet_vehicles_max"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Quantidade de Veículos</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 10, 50, 100"
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Criando..." : "Criar Plano"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}